import React, { useState, useEffect, useRef } from 'react';
import { Upload, Save, RefreshCw, AlertTriangle, X, CheckCircle, FileDown, MapPin } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  Box,
  CircularProgress,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import MainService from '../../services/MainService';

const LocationUpload = () => {
  const [locations, setLocations] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState({
    EmirateId: '',
    AreaId: ''
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadEmirates();
    loadAreas();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadEmirates = async () => {
    try {
      const response = await MainService.EmiratesInDropDown();
      setEmirates(response || []);
    } catch (err) {
      console.error('Error loading emirates:', err);
    }
  };

  const loadAreas = async () => {
    try {
      const response = await MainService.AreasInDropDown();
      setAreas(response || []);
    } catch (err) {
      console.error('Error loading areas:', err);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const processedLocations = jsonData.map((row) => ({
          Name: row.Name || '',
          NameAr: row.NameAr || '',
          EmirateId: '',
          AreaId: ''
        }));

        setLocations(processedLocations);
        setSuccess(`Loaded ${processedLocations.length} locations from Excel`);
      } catch (err) {
        console.error('Error parsing Excel:', err);
        setError('Failed to parse Excel file. Please check the format.');
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleLocationFieldChange = (index, field, value) => {
    setLocations(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  const handleApply = () => {
    if (!filter.EmirateId && !filter.AreaId) {
      setError('Please select at least one filter (Emirate or Area)');
      return;
    }

    setLocations(prev => prev.map(location => ({
      ...location,
      EmirateId: filter.EmirateId || location.EmirateId,
      AreaId: filter.AreaId || location.AreaId
    })));
    setSuccess('Applied filters to all locations');
  };

  const handleInit = () => {
    setLocations([]);
    setFilter({ EmirateId: '', AreaId: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadLocations = async () => {
    // Validation
    const invalidLocations = locations.filter(location => 
      !location.EmirateId || !location.AreaId || !location.Name
    );

    if (invalidLocations.length > 0) {
      setError('Invalid Format: Emirate, Area, and Name are required for all locations');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await MainService.UploadBulkLocations(locations);

      if (response === 'Success') {
        setSuccess('Locations uploaded successfully');
        handleInit();
      } else {
        setError(response || 'Failed to upload locations');
      }
    } catch (err) {
      console.error('Error uploading locations:', err);
      setError('Failed to upload locations');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL || ''}/excel/BulkLocation.xlsx`;
    link.download = 'BulkLocation.xlsx';
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Location Upload</h1>
            <p className="text-sm text-gray-500 mt-1">Upload locations from Excel file</p>
          </div>
          <Button
            variant="outlined"
            startIcon={<FileDown className="w-4 h-4" />}
            onClick={downloadTemplate}
            sx={{
              borderColor: '#46955A',
              color: '#46955A',
              '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
            }}
          >
            Download Excel Template
          </Button>
        </div>

        {/* File Upload and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Choose Excel File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#46955A] text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Emirate</label>
            <FormControl fullWidth size="small">
              <Select
                value={filter.EmirateId}
                onChange={(e) => setFilter(prev => ({ ...prev, EmirateId: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#46955A',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#46955A',
                    },
                  },
                }}
              >
                <MenuItem value="">Select Emirate</MenuItem>
                {emirates.map((emi) => (
                  <MenuItem key={emi.Id} value={emi.Id}>{emi.Name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Area</label>
            <FormControl fullWidth size="small">
              <Select
                value={filter.AreaId}
                onChange={(e) => setFilter(prev => ({ ...prev, AreaId: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#46955A',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#46955A',
                    },
                  },
                }}
              >
                <MenuItem value="">Select Area</MenuItem>
                {areas.map((area) => (
                  <MenuItem key={area.Id} value={area.Id}>{area.Name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="flex items-end">
            <Button
              variant="outlined"
              onClick={handleApply}
              disabled={locations.length === 0}
              fullWidth
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>

      {/* Locations Table - Excel Style */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: '600px' }}>
          <table className="w-full border-collapse" style={{ minWidth: '800px' }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '40px' }}>#</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '200px' }}>Name</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '200px' }}>Name Ar</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Emirate</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Area</th>
              </tr>
            </thead>
            <tbody>
              {locations.length > 0 ? (
                locations.map((location, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-2 py-1 text-sm text-center bg-gray-50">{index + 1}</td>
                    <td className={`border border-gray-300 px-1 py-0 ${!location.Name ? 'bg-red-100' : ''}`}>
                      <input
                        type="text"
                        value={location.Name || ''}
                        onChange={(e) => handleLocationFieldChange(index, 'Name', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '200px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={location.NameAr || ''}
                        onChange={(e) => handleLocationFieldChange(index, 'NameAr', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '200px' }}
                      />
                    </td>
                    <td className={`border border-gray-300 px-1 py-0 ${!location.EmirateId ? 'bg-red-100' : ''}`}>
                      <select
                        value={location.EmirateId || ''}
                        onChange={(e) => handleLocationFieldChange(index, 'EmirateId', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent"
                        style={{ minWidth: '150px' }}
                      >
                        <option value="">Select...</option>
                        {emirates.map((emi) => (
                          <option key={emi.Id} value={emi.Id}>{emi.Name}</option>
                        ))}
                      </select>
                    </td>
                    <td className={`border border-gray-300 px-1 py-0 ${!location.AreaId ? 'bg-red-100' : ''}`}>
                      <select
                        value={location.AreaId || ''}
                        onChange={(e) => handleLocationFieldChange(index, 'AreaId', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent"
                        style={{ minWidth: '150px' }}
                      >
                        <option value="">Select...</option>
                        {areas.map((area) => (
                          <option key={area.Id} value={area.Id}>{area.Name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="border border-gray-300 px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <Upload className="w-12 h-12 mb-3 opacity-50" />
                      <p className="text-sm font-medium">No locations loaded</p>
                      <p className="text-xs mt-1">Upload an Excel file to get started</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-center gap-3">
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} sx={{ color: '#46955A' }} />
              <span className="text-sm text-gray-600">Processing...</span>
            </Box>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<RefreshCw className="w-4 h-4" />}
                onClick={handleInit}
                sx={{
                  borderColor: '#46955A',
                  color: '#46955A',
                  '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Upload className="w-4 h-4" />}
                onClick={handleUploadLocations}
                disabled={locations.length === 0}
                sx={{
                  backgroundColor: '#46955A',
                  '&:hover': { backgroundColor: '#3d7d4f' },
                  '&:disabled': { backgroundColor: '#9CA3AF' }
                }}
              >
                Upload Locations
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationUpload;

